import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recruiterAPI } from '@/services/api';
import { ChevronLeft, Search, Filter, Archive, Trash2, RotateCcw, Eye, History as HistoryIcon } from 'lucide-react';
import Pagination from '@/components/shared/Pagination';

const STATUS_OPTIONS = [
  { value: 'archived', label: 'Archived only' },
  { value: 'closed', label: 'Closed only' },
  { value: 'all', label: 'Archived + Closed' },
];

export default function RecruiterJobHistory() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    q: '', skill: '', job_type: '', status: 'all', date_from: '', date_to: '',
    min_comp: '', max_comp: '', sort_by: 'updated_at', sort_order: 'desc',
  });
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [acting, setActing] = useState(false);
  const debounce = useRef(null);

  const load = useCallback(async (p = 1, ps = pageSize) => {
    setLoading(true); setError('');
    try {
      const params = { ...filters, skip: (p - 1) * ps, limit: ps };
      const data = await recruiterAPI.getArchivedJobs(params);
      setJobs(data.jobs);
      setTotal(data.total);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load history');
    } finally { setLoading(false); }
  }, [filters, pageSize]);

  // Debounced auto-refresh on filter changes
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setPage(1); load(1, pageSize); }, 300);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [filters]);

  const update = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));

  const restore = async (job) => {
    setActing(true);
    try { await recruiterAPI.restoreJob(job.id); await load(0, false); }
    catch (e) { alert(e?.response?.data?.detail || 'Restore failed'); }
    finally { setActing(false); }
  };

  const remove = async () => {
    if (!confirm?.job) return;
    setActing(true);
    try { await recruiterAPI.deleteJob(confirm.job.id); setConfirm(null); await load(0, false); }
    catch (e) { alert(e?.response?.data?.detail || 'Delete failed'); }
    finally { setActing(false); }
  };

  return (
    <div data-testid="recruiter-job-history">
      <button onClick={() => navigate('/recruiter/jobs')} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white text-sm flex items-center gap-1 mb-4" data-testid="back-to-jobs">
        <ChevronLeft className="w-4 h-4" /> Back to active jobs
      </button>

      <div className="flex items-center gap-2 mb-1">
        <HistoryIcon className="w-5 h-5 text-amber-700 dark:text-amber-300" />
        <span className="text-xs uppercase tracking-widest text-amber-300/80">Archive</span>
      </div>
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Job History</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Search archived and closed job postings across your organization. Restore or permanently delete from here.</p>

      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-3 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-zinc-500" />
            <input value={filters.q} onChange={e => update('q', e.target.value)} placeholder="Search by title, description, or requirements…" data-testid="history-search-q"
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" />
          </div>
          <FilterField label="Skill / keyword">
            <input value={filters.skill} onChange={e => update('skill', e.target.value)} placeholder="e.g. Python" data-testid="history-search-skill"
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" />
          </FilterField>
          <FilterField label="Job type">
            <select value={filters.job_type} onChange={e => update('job_type', e.target.value)} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="history-job-type">
              <option value="">All</option>
              <option value="permanent">Permanent</option>
              <option value="contractual">Contractual</option>
              <option value="freelancing">Freelancing</option>
            </select>
          </FilterField>
          <FilterField label="Status">
            <select value={filters.status} onChange={e => update('status', e.target.value)} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="history-status">
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </FilterField>
          <FilterField label="Created from">
            <input type="date" value={filters.date_from} onChange={e => update('date_from', e.target.value)} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="history-date-from" />
          </FilterField>
          <FilterField label="Created to">
            <input type="date" value={filters.date_to} onChange={e => update('date_to', e.target.value)} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="history-date-to" />
          </FilterField>
          <FilterField label="Min comp">
            <input type="number" value={filters.min_comp} onChange={e => update('min_comp', e.target.value)} placeholder="0" className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" />
          </FilterField>
          <FilterField label="Max comp">
            <input type="number" value={filters.max_comp} onChange={e => update('max_comp', e.target.value)} placeholder="999999" className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" />
          </FilterField>
          <FilterField label="Sort">
            <div className="flex gap-2">
              <select value={filters.sort_by} onChange={e => update('sort_by', e.target.value)} className="flex-1 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-2 py-2 text-sm text-slate-900 dark:text-white">
                <option value="updated_at">Last update</option>
                <option value="created_at">Created</option>
                <option value="title">Title</option>
              </select>
              <select value={filters.sort_order} onChange={e => update('sort_order', e.target.value)} className="w-24 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-2 py-2 text-sm text-slate-900 dark:text-white">
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
              </select>
            </div>
          </FilterField>
        </div>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 px-4 py-2 rounded-lg mb-4 text-sm">{error}</div>}

      <div className="flex items-center justify-between mb-3">
        <p className="text-slate-500 dark:text-zinc-500 text-xs">{loading ? 'Searching…' : `${total} result${total === 1 ? '' : 's'}`}</p>
      </div>

      {jobs.length === 0 && !loading ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-10 text-center" data-testid="history-empty">
          <Filter className="w-10 h-10 text-slate-600 dark:text-zinc-700 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-zinc-500">No matching historical jobs found</p>
          <p className="text-slate-500 dark:text-zinc-600 text-xs mt-1">Try a broader search or change the status filter.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map(j => (
            <div key={j.id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4" data-testid={`history-row-${j.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-slate-900 dark:text-white font-medium truncate">{j.title}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${j.status === 'archived' ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400'}`}>{j.status}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400">{j.job_type}</span>
                  </div>
                  <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1 line-clamp-2">{j.description}</p>
                  <p className="text-slate-500 dark:text-zinc-600 text-[11px] mt-1">
                    Created {new Date(j.created_at).toLocaleDateString()}
                    {j.archived_at && ` · Archived ${new Date(j.archived_at).toLocaleDateString()}`}
                    {j.compensation_min ? ` · $${j.compensation_min}-$${j.compensation_max}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => navigate(`/recruiter/jobs/${j.id}`)} className="p-2 text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid={`history-view-${j.id}`} title="View">
                    <Eye className="w-4 h-4" />
                  </button>
                  {j.status === 'archived' && (
                    <button onClick={() => restore(j)} disabled={acting} className="p-2 text-slate-500 dark:text-zinc-500 hover:text-emerald-700 dark:hover:text-emerald-300" data-testid={`history-restore-${j.id}`} title="Restore">
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => setConfirm({ job: j })} className="p-2 text-slate-500 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400" data-testid={`history-delete-${j.id}`} title="Delete permanently">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {total > pageSize && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={(p) => { setPage(p); load(p, pageSize); }}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); load(1, s); }}
          testIdPrefix="recruiter-history-pg"
        />
      )}

      {confirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setConfirm(null)} data-testid="history-confirm">
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Delete this job permanently?</h3>
            <p className="text-slate-500 dark:text-zinc-400 text-sm mb-5">&ldquo;{confirm.job.title}&rdquo; and all associated matches, screenings, and interviews will be removed. This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirm(null)} className="flex-1 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm">Cancel</button>
              <button onClick={remove} disabled={acting} className="flex-1 py-2 bg-red-500 hover:bg-red-400 text-white rounded-lg text-sm font-medium disabled:opacity-50" data-testid="history-confirm-delete">
                {acting ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] text-slate-500 dark:text-zinc-500 mb-1 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}
